import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import StoreUser from "../../../types/StoreUser";
import { useAnalyticsEvents } from "../../../database/analytics/getAnalyticsEvents";
import {
  calculateMetrics,
  getActiveUsersInDateRange,
  getPayingActiveUsersInDateRange,
  countPayingUsers,
  getNewUsersInDateRange,
  getTopEvents,
  getEventTrend,
} from "../../../database/analytics/calculateMetrics";
import usePayingMembers from "../../../database/analytics/usePayingMembers";
import {
  getEventsByCategory,
  calculateFunnelConversion,
  FUNNELS,
} from "../../../utils/analyticsEventCategories";
import {
  calculateHealthMetrics,
  generateInsights,
  Insight,
  InsightPriority,
} from "../../../utils/analyticsInsights";
import {
  DesignSystemSurface,
  PageHeader,
  SectionHeader,
  Card,
  Tag,
  Skeleton,
  Button,
} from "../../../design-system";
import {
  IconChart,
  IconUsers,
  IconSparkles,
  IconCalendar,
  IconChevronDown,
} from "../../../design-system/icons/icons";
import RevenueTab from "./RevenueTab";
import ExperimentsTab from "./ExperimentsTab";

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FilterRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const SelectShell = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  &:focus-within {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }

  span {
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
  }
`;

const NativeSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  padding-right: var(--space-1);
  cursor: pointer;
`;

const TabBar = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-6);
  max-width: 100%;
  overflow-x: auto;
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-surface);
      color: var(--color-text);
      font-weight: var(--font-weight-semibold);
      box-shadow: var(--shadow-xs);
    `}
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
`;

const StatLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const StatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--color-accent-subtle);
  color: var(--color-accent-text);
  border-radius: var(--radius-sm);
`;

const StatValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
  margin-top: var(--space-3);
`;

const StatHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: var(--space-2);
`;

const TrendChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  height: 220px;
  padding: var(--space-4) 0 var(--space-2);
`;

const TrendCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
`;

const TrendBar = styled.div<{ $active: boolean }>`
  width: 100%;
  background: ${(p) =>
    p.$active ? "var(--color-accent)" : "var(--color-accent-subtle)"};
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  transition: height var(--duration-base) var(--ease-standard);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: var(--space-1);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: ${(p) =>
    p.$active ? "var(--color-text-inverse)" : "var(--color-accent-text)"};
`;

const TrendLabel = styled.div<{ $active: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: ${(p) =>
    p.$active ? "var(--color-text)" : "var(--color-text-tertiary)"};
  font-weight: ${(p) =>
    p.$active ? "var(--font-weight-semibold)" : "var(--font-weight-regular)"};
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const EventName = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const EventMeta = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
`;

const EventCount = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent-text);
  white-space: nowrap;
`;

const FunnelRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-2);
`;

const FunnelLabel = styled.div`
  flex: 1;
  min-width: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const FunnelSubLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
`;

const FunnelCount = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

const FunnelPct = styled.div<{ $rate: number; $isFirst: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  min-width: 64px;
  text-align: right;
  color: ${(p) =>
    p.$isFirst
      ? "var(--color-accent-text)"
      : p.$rate > 50
      ? "var(--color-success)"
      : p.$rate > 25
      ? "var(--color-warning)"
      : "var(--color-danger)"};
`;

const FunnelTrack = styled.div`
  width: 100%;
  height: 32px;
  background: var(--color-surface-inset);
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-bottom: var(--space-4);
`;

const FunnelFill = styled.div<{ $rate: number; $isFirst: boolean }>`
  height: 100%;
  width: ${(p) => p.$rate}%;
  background: ${(p) =>
    p.$isFirst
      ? "var(--color-accent)"
      : p.$rate > 50
      ? "var(--color-success)"
      : p.$rate > 25
      ? "var(--color-warning)"
      : "var(--color-danger)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  transition: width var(--duration-slow) var(--ease-standard);
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--space-3);
  margin-top: var(--space-4);
`;

const PlatformChip = styled.div`
  padding: var(--space-3);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
`;

const PlatformValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
  margin-top: 2px;
`;

const ConversionCallout = styled.div`
  margin-top: var(--space-5);
  padding: var(--space-5);
  background: var(--color-accent-subtle);
  border: 1px solid transparent;
  border-radius: var(--radius-lg);
`;

const ConversionLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent-text);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ConversionValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent-text);
  line-height: var(--line-height-tight);
  margin-top: var(--space-2);
`;

const ConversionHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-top: var(--space-2);
`;

const SectionSpacing = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const InsightItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const InsightHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

const InsightTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const InsightWhy = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const InsightAction = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  padding: var(--space-3);
  background: var(--color-accent-subtle);
  border-radius: var(--radius-md);
`;

const PRIORITY_TAG: Record<
  InsightPriority,
  { variant: "danger" | "warning" | "neutral"; label: string }
> = {
  high: { variant: "danger", label: "High impact" },
  medium: { variant: "warning", label: "Medium" },
  low: { variant: "neutral", label: "Low" },
};

const InsightList: React.FC<{ insights: Insight[] }> = ({ insights }) => (
  <div>
    {insights.map((insight) => {
      const tag = PRIORITY_TAG[insight.priority];
      return (
        <InsightItem key={insight.id}>
          <InsightHeader>
            <Tag variant={tag.variant} size="sm" withDot>
              {tag.label}
            </Tag>
            <Tag variant="neutral" size="sm">
              {insight.area}
            </Tag>
            <InsightTitle>{insight.title}</InsightTitle>
          </InsightHeader>
          <InsightWhy>{insight.why}</InsightWhy>
          <InsightAction>{insight.action}</InsightAction>
        </InsightItem>
      );
    })}
  </div>
);

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

type FilterProps = {
  dateRange: string;
  setDateRange: (v: string) => void;
  eventFilter: string;
  setEventFilter: (v: string) => void;
  eventTypes: string[];
  count: number;
  loading: boolean;
  showEventDetails?: boolean;
};

const Filters: React.FC<FilterProps> = ({
  dateRange,
  setDateRange,
  eventFilter,
  setEventFilter,
  eventTypes,
  count,
  loading,
  showEventDetails = true,
}) => (
  <FilterRow>
    <SelectShell>
      <IconCalendar size={14} />
      <span>Range</span>
      <NativeSelect
        value={dateRange}
        onChange={(e) => setDateRange(e.target.value)}
      >
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="365">Last year</option>
      </NativeSelect>
      <IconChevronDown size={12} />
    </SelectShell>
    {showEventDetails && (
      <>
        <SelectShell>
          <span>Event</span>
          <NativeSelect
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
          >
            <option value="all">All events</option>
            {eventTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </NativeSelect>
          <IconChevronDown size={12} />
        </SelectShell>
        <Tag variant="neutral" size="sm">
          {loading ? "Loading…" : `${count.toLocaleString()} events`}
        </Tag>
      </>
    )}
  </FilterRow>
);

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  hint?: string;
  loading?: boolean;
}> = ({ label, value, icon, hint, loading }) => (
  <Card padding="md">
    <StatLabel>
      {icon && <StatIcon>{icon}</StatIcon>}
      {label}
    </StatLabel>
    {loading ? (
      <div style={{ marginTop: "var(--space-3)" }}>
        <Skeleton width="60%" height={28} />
      </div>
    ) : (
      <StatValue>
        {typeof value === "number" ? value.toLocaleString() : value}
      </StatValue>
    )}
    {hint && <StatHint>{hint}</StatHint>}
  </Card>
);

type Props = {
  user: StoreUser;
};

type TabDef = { id: number; label: string; icon?: React.ReactNode };
const TABS: TabDef[] = [
  { id: 0, label: "Overview", icon: <IconChart size={14} /> },
  { id: 1, label: "Events" },
  { id: 2, label: "Users", icon: <IconUsers size={14} /> },
  { id: 3, label: "Platforms" },
  { id: 4, label: "Insights", icon: <IconSparkles size={14} /> },
  { id: 5, label: "Revenue", icon: <IconChart size={14} /> },
  { id: 6, label: "Experiments", icon: <IconSparkles size={14} /> },
];

const AnalyticsScreen = ({ user }: Props) => {
  const [currentTabIndex, setTabIndex] = useState(0);
  const [dateRange, setDateRange] = useState<string>("30");
  const [eventFilter, setEventFilter] = useState<string>("all");

  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return { startDate: start, endDate: end };
  }, [dateRange]);

  const { events, loading, error } = useAnalyticsEvents({
    communityId: user?.communityId,
    startDate,
    endDate,
    eventType: eventFilter,
    limit: 5000,
  });

  const metrics = useMemo(() => calculateMetrics(events), [events]);
  const topEvents = useMemo(() => getTopEvents(events, 10), [events]);
  // Funnels must see every event name. Feeding them the top-10 slice made any
  // step outside a community's loudest events read 0 (signup events fire once
  // per user and always lose to per-step lesson telemetry).
  const allEventCounts = useMemo(
    () => getTopEvents(events, Number.MAX_SAFE_INTEGER),
    [events]
  );
  const eventTrend = useMemo(() => getEventTrend(events, 7), [events]);
  // Category totals need every event name too. On the top-10 slice a whole
  // category (the WhatsApp coach opt-in, say) could total zero while the app
  // was emitting it all week.
  const eventsByCategory = useMemo(
    () => getEventsByCategory(allEventCounts),
    [allEventCounts]
  );
  const monetizationFunnel = useMemo(
    () => calculateFunnelConversion(allEventCounts, "MONETIZATION_FUNNEL"),
    [allEventCounts]
  );
  const signupFunnel = useMemo(
    () => calculateFunnelConversion(allEventCounts, "SIGNUP_FUNNEL"),
    [allEventCounts]
  );
  const planFunnel = useMemo(
    () => calculateFunnelConversion(allEventCounts, "PLAN_ENGAGEMENT_FUNNEL"),
    [allEventCounts]
  );
  const timerFunnel = useMemo(
    () => calculateFunnelConversion(allEventCounts, "TIMER_USAGE_FUNNEL"),
    [allEventCounts]
  );
  const landingFunnel = useMemo(
    () => calculateFunnelConversion(allEventCounts, "LANDING_FUNNEL"),
    [allEventCounts]
  );
  const winbackFunnel = useMemo(
    () => calculateFunnelConversion(allEventCounts, "WINBACK_FUNNEL"),
    [allEventCounts]
  );

  const health = useMemo(() => calculateHealthMetrics(events), [events]);
  const insights = useMemo(
    () => generateInsights(events, health),
    [events, health]
  );

  const last30DaysActiveUsers = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return getActiveUsersInDateRange(events, start, end);
  }, [events]);
  const last7DaysActiveUsers = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return getActiveUsersInDateRange(events, start, end);
  }, [events]);
  const last24HoursActiveUsers = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setHours(start.getHours() - 24);
    return getActiveUsersInDateRange(events, start, end);
  }, [events]);
  const newUsersThisMonth = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return getNewUsersInDateRange(events, start, end);
  }, [events]);

  // Hard-paywall communities (config.subRequired): raw active-user counts also
  // include visitors stuck at the paywall who never subscribed, so also compute
  // paying-only active users. `paying` is null-ish (subRequired false) for open
  // communities, leaving the cards unchanged.
  const paying = usePayingMembers(user?.communityId);
  const payingActive = useMemo(() => {
    if (!paying.subRequired) return null;
    const now = new Date();
    const sinceDays = (n: number) => {
      const s = new Date();
      s.setDate(s.getDate() - n);
      return getPayingActiveUsersInDateRange(events, s, now, paying.subscribedIds);
    };
    const mau = sinceDays(30);
    const wau = sinceDays(7);
    const since24h = new Date();
    since24h.setHours(since24h.getHours() - 24);
    const dau = getPayingActiveUsersInDateRange(
      events,
      since24h,
      now,
      paying.subscribedIds
    );
    return {
      total: countPayingUsers(events, paying.subscribedIds),
      active30: mau,
      active7: wau,
      active24: dau,
      stickinessPct: mau > 0 ? (dau / mau) * 100 : 0,
    };
  }, [events, paying.subRequired, paying.subscribedIds]);

  const eventTypes = Object.keys(metrics.eventsByType);

  const filtersEl = (
    <Filters
      dateRange={dateRange}
      setDateRange={setDateRange}
      eventFilter={eventFilter}
      setEventFilter={setEventFilter}
      eventTypes={eventTypes}
      count={events.length}
      loading={loading}
      showEventDetails={currentTabIndex <= 4}
    />
  );

  const renderTrend = () => {
    const maxCount = Math.max(...eventTrend.map((t) => t.count), 1);
    return (
      <Card padding="lg">
        <SectionHeader
          title="7-day event trend"
          description="Activity volume over the past week."
        />
        <TrendChart>
          {eventTrend.map((item, index) => {
            const heightPct = (item.count / maxCount) * 100;
            const isToday = index === eventTrend.length - 1;
            const [d, m] = item.date.split("/");
            return (
              <TrendCol key={index}>
                <TrendBar
                  $active={isToday}
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                >
                  {item.count > 0 ? item.count : ""}
                </TrendBar>
                <TrendLabel $active={isToday}>
                  {m}/{d}
                </TrendLabel>
              </TrendCol>
            );
          })}
        </TrendChart>
      </Card>
    );
  };

  const renderOverview = () => (
    <SectionSpacing>
      <StatGrid>
        <StatCard
          label={payingActive ? "Paying users" : "Total users"}
          value={payingActive ? payingActive.total : metrics.totalUsers}
          icon={<IconUsers size={14} />}
          hint={
            payingActive
              ? `${metrics.totalUsers.toLocaleString()} opened the app (incl. paywall)`
              : "All-time unique users"
          }
          loading={loading || paying.loading}
        />
        <StatCard
          label={payingActive ? "Active paying (30d)" : "Active (30d)"}
          value={payingActive ? payingActive.active30 : last30DaysActiveUsers}
          icon={<IconChart size={14} />}
          hint={
            payingActive
              ? `${last30DaysActiveUsers.toLocaleString()} incl. paywall visitors`
              : "Unique users in last 30 days"
          }
          loading={loading || paying.loading}
        />
        <StatCard
          label="Total events"
          value={metrics.totalEvents}
          hint="In selected range"
          loading={loading}
        />
        <StatCard
          label="New signups"
          value={metrics.newUsers}
          hint="In selected range"
          loading={loading}
        />
      </StatGrid>

      <StatGrid>
        <StatCard
          label={payingActive ? "Active paying (24h)" : "Active (24h)"}
          value={payingActive ? payingActive.active24 : health.dau}
          hint={
            payingActive
              ? `${health.dau.toLocaleString()} incl. paywall visitors`
              : "Unique users in the last day"
          }
          loading={loading || paying.loading}
        />
        <StatCard
          label={payingActive ? "Active paying (7d)" : "Active (7d)"}
          value={payingActive ? payingActive.active7 : health.wau}
          hint={
            payingActive
              ? `${health.wau.toLocaleString()} incl. paywall visitors`
              : health.wauChangePct !== null
                ? `${health.wauChangePct >= 0 ? "+" : ""}${health.wauChangePct.toFixed(0)}% vs previous week`
                : "Last 7 days"
          }
          loading={loading || paying.loading}
        />
        <StatCard
          label={payingActive ? "Stickiness (paying)" : "Stickiness"}
          value={`${(payingActive ? payingActive.stickinessPct : health.stickinessPct).toFixed(1)}%`}
          hint="Daily actives as share of monthly (DAU/MAU)"
          loading={loading || paying.loading}
        />
        <StatCard
          label="Installs"
          value={metrics.eventsByType["app_install"] || 0}
          hint="app_install events in range"
          loading={loading}
        />
      </StatGrid>
      {payingActive && (
        <p
          style={{
            margin: "0",
            fontSize: "12px",
            color: "var(--color-text-secondary)",
          }}
        >
          This app has a hard paywall, so active-user cards count only members
          with an active subscription. The dimmed figures include everyone who
          opened the app, including visitors who stopped at the paywall.
        </p>
      )}

      {error ? (
        <Card padding="lg">
          <Tag variant="danger" withDot>
            Error
          </Tag>
          <p style={{ marginTop: "var(--space-3)", color: "var(--color-text-secondary)" }}>
            {error}
          </p>
        </Card>
      ) : (
        <>
          {!loading && (
            <Card padding="lg">
              <SectionHeader
                title="What to work on next"
                description="Automatic reading of your funnels and activity for this range."
                actions={
                  insights.length > 3 ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setTabIndex(4)}
                    >
                      All {insights.length} insights
                    </Button>
                  ) : undefined
                }
              />
              <InsightList insights={insights.slice(0, 3)} />
            </Card>
          )}

          {renderTrend()}

          <Card padding="lg">
            <SectionHeader
              title="Top events"
              description="Highest-volume events in the selected range."
              actions={
                topEvents.length > 0 ? (
                  <Tag variant="neutral" size="sm">
                    {topEvents.length} events
                  </Tag>
                ) : undefined
              }
            />
            {topEvents.length === 0 ? (
              <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
                No events found in the selected range.
              </p>
            ) : (
              <EventList>
                {topEvents.map((item, index) => (
                  <EventRow key={index}>
                    <div>
                      <EventName>{item.event}</EventName>
                      <EventMeta>
                        {((item.count / metrics.totalEvents) * 100).toFixed(1)}% of total
                      </EventMeta>
                    </div>
                    <EventCount>{item.count.toLocaleString()}</EventCount>
                  </EventRow>
                ))}
              </EventList>
            )}
          </Card>
        </>
      )}
    </SectionSpacing>
  );

  const renderEvents = () => (
    <SectionSpacing>
      <Card padding="lg">
        <SectionHeader
          title="Event breakdown"
          description="Counts by event type for the selected range."
        />
        {Object.keys(metrics.eventsByType).length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
            No events found.
          </p>
        ) : (
          <EventList>
            {Object.entries(metrics.eventsByType)
              .sort((a, b) => b[1] - a[1])
              .map(([eventType, count]) => (
                <EventRow key={eventType}>
                  <div>
                    <EventName>{eventType}</EventName>
                    <EventMeta>
                      {((count / metrics.totalEvents) * 100).toFixed(1)}% of total
                    </EventMeta>
                  </div>
                  <EventCount>{count.toLocaleString()}</EventCount>
                </EventRow>
              ))}
          </EventList>
        )}
      </Card>

      <Card padding="lg">
        <SectionHeader
          title="Recent events"
          description="Latest 50 events captured."
        />
        {events.length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
            No events found.
          </p>
        ) : (
          <EventList>
            {events.slice(0, 50).map((event) => (
              <EventRow key={event.id}>
                <div>
                  <EventName>{event.event}</EventName>
                  <EventMeta>
                    {event.platform || "—"} · {new Date(event.ts).toLocaleString()}
                  </EventMeta>
                </div>
                <EventCount>
                  {event.userId ? event.userId.substring(0, 8) + "…" : "—"}
                </EventCount>
              </EventRow>
            ))}
          </EventList>
        )}
      </Card>
    </SectionSpacing>
  );

  const renderUsers = () => (
    <SectionSpacing>
      <StatGrid>
        <StatCard label="New users" value={newUsersThisMonth} hint="This month" loading={loading} />
        <StatCard label="Active (7d)" value={last7DaysActiveUsers} hint="Last 7 days" loading={loading} />
        <StatCard label="Active (24h)" value={last24HoursActiveUsers} hint="Last 24 hours" loading={loading} />
        <StatCard label="Total users" value={metrics.totalUsers} hint="All time" loading={loading} />
      </StatGrid>

      <Card padding="lg">
        <SectionHeader title="Signup providers" />
        {Object.keys(metrics.signupsByProvider).length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
            No signup data available.
          </p>
        ) : (
          <EventList>
            {Object.entries(metrics.signupsByProvider).map(([provider, count]) => (
              <EventRow key={provider}>
                <EventName style={{ textTransform: "capitalize" }}>{provider}</EventName>
                <EventCount>{count.toLocaleString()}</EventCount>
              </EventRow>
            ))}
          </EventList>
        )}
      </Card>
    </SectionSpacing>
  );

  const renderPlatforms = () => (
    <SectionSpacing>
      <Card padding="lg">
        <SectionHeader
          title="By platform"
          description="Event share per device platform."
        />
        {Object.keys(metrics.usersByPlatform).length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
            No platform data available.
          </p>
        ) : (
          <PlatformGrid>
            {Object.entries(metrics.usersByPlatform).map(([platform, count]) => {
              const pct = (count / metrics.totalEvents) * 100;
              return (
                <PlatformChip key={platform}>
                  <Tag variant="accent" size="sm">
                    {platform}
                  </Tag>
                  <PlatformValue>{count.toLocaleString()}</PlatformValue>
                  <StatHint>{pct.toFixed(1)}% of events</StatHint>
                </PlatformChip>
              );
            })}
          </PlatformGrid>
        )}
      </Card>
    </SectionSpacing>
  );

  const renderFunnel = (
    title: string,
    description: string,
    steps: { step: string; count: number; conversionRate: number }[],
    highlight?: boolean
  ) => {
    const hasData = steps.some((s) => s.count > 0);
    const overall =
      steps.length > 0 ? steps[steps.length - 1].conversionRate : 0;
    return (
      <Card padding="lg" elevation={highlight ? "md" : "sm"}>
        <SectionHeader
          title={title}
          description={description}
          actions={
            highlight ? (
              <Tag variant="accent" size="sm" withDot>
                Key metric
              </Tag>
            ) : undefined
          }
        />
        {!hasData ? (
          <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
            No data available yet.
          </p>
        ) : (
          <>
            {steps.map((step, index) => {
              const isFirst = index === 0;
              const prevStep = index > 0 ? steps[index - 1] : null;
              const stepPct = prevStep
                ? ((step.count / Math.max(prevStep.count, 1)) * 100).toFixed(1)
                : "100.0";
              return (
                <div key={step.step}>
                  <FunnelRow>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <FunnelLabel>{step.step}</FunnelLabel>
                      {!isFirst && (
                        <FunnelSubLabel>
                          {stepPct}% from previous step
                        </FunnelSubLabel>
                      )}
                    </div>
                    <FunnelCount>{step.count.toLocaleString()} users</FunnelCount>
                    <FunnelPct $rate={step.conversionRate} $isFirst={isFirst}>
                      {step.conversionRate.toFixed(1)}%
                    </FunnelPct>
                  </FunnelRow>
                  <FunnelTrack>
                    <FunnelFill
                      $rate={Math.max(step.conversionRate, 1)}
                      $isFirst={isFirst}
                    >
                      {step.count > 0 ? step.count.toLocaleString() : ""}
                    </FunnelFill>
                  </FunnelTrack>
                </div>
              );
            })}
            {highlight && steps[steps.length - 1].count > 0 && (
              <ConversionCallout>
                <ConversionLabel>Overall conversion rate</ConversionLabel>
                <ConversionValue>{overall.toFixed(2)}%</ConversionValue>
                <ConversionHint>
                  {steps[steps.length - 1].count.toLocaleString()} subscribers
                  from {steps[0].count.toLocaleString()} installs
                </ConversionHint>
              </ConversionCallout>
            )}
          </>
        )}
      </Card>
    );
  };

  const renderInsights = () => (
    <SectionSpacing>
      <Card padding="lg">
        <SectionHeader
          title="Recommendations"
          description="What to work on next, ranked by impact. Computed from the funnels and activity below."
          actions={
            <Tag variant="accent" size="sm" withDot>
              {insights.filter((i) => i.priority === "high").length} high impact
            </Tag>
          }
        />
        <InsightList insights={insights} />
      </Card>

      {renderFunnel(
        FUNNELS.MONETIZATION_FUNNEL.name,
        "Critical conversion from app install to active subscription.",
        monetizationFunnel,
        true
      )}

      <Card padding="lg">
        <SectionHeader
          title="Event categories"
          description="Events grouped by category for behavior patterns."
        />
        {Object.keys(eventsByCategory).length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
            No categorized events available.
          </p>
        ) : (
          <PlatformGrid>
            {Object.entries(eventsByCategory)
              .sort((a, b) => b[1] - a[1])
              .map(([category, count]) => {
                const total = Object.values(eventsByCategory).reduce(
                  (a, b) => a + b,
                  0
                );
                const pct = ((count / total) * 100).toFixed(1);
                return (
                  <PlatformChip key={category}>
                    <Tag variant="neutral" size="sm">
                      {category}
                    </Tag>
                    <PlatformValue>{count.toLocaleString()}</PlatformValue>
                    <StatHint>{pct}% of events</StatHint>
                  </PlatformChip>
                );
              })}
          </PlatformGrid>
        )}
      </Card>

      <TwoCol>
        {renderFunnel(
          FUNNELS.SIGNUP_FUNNEL.name,
          "From sign-up button through onboarding completion.",
          signupFunnel
        )}
        {renderFunnel(
          FUNNELS.PLAN_ENGAGEMENT_FUNNEL.name,
          "Plan discovery through first step completion.",
          planFunnel
        )}
      </TwoCol>

      <TwoCol>
        {renderFunnel(
          FUNNELS.LANDING_FUNNEL.name,
          "Web landing page visits through app install.",
          landingFunnel
        )}
        {renderFunnel(
          FUNNELS.WINBACK_FUNNEL.name,
          "Paywall declines caught by the winback offer.",
          winbackFunnel
        )}
      </TwoCol>

      {renderFunnel(
        FUNNELS.TIMER_USAGE_FUNNEL.name,
        "Timer feature adoption from view to completion.",
        timerFunnel
      )}
    </SectionSpacing>
  );

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Analytics"
            subtitle="How your community is engaging — events, users, funnels."
            actions={filtersEl}
          />

          <TabBar role="tablist">
            {TABS.map((tab) => (
              <TabButton
                key={tab.id}
                type="button"
                role="tab"
                $active={currentTabIndex === tab.id}
                aria-selected={currentTabIndex === tab.id}
                onClick={() => setTabIndex(tab.id)}
              >
                {tab.icon}
                {tab.label}
              </TabButton>
            ))}
          </TabBar>

          {currentTabIndex === 0 && renderOverview()}
          {currentTabIndex === 1 && renderEvents()}
          {currentTabIndex === 2 && renderUsers()}
          {currentTabIndex === 3 && renderPlatforms()}
          {currentTabIndex === 4 && renderInsights()}
          {currentTabIndex === 5 && (
            <RevenueTab communityId={user?.communityId} />
          )}
          {currentTabIndex === 6 && (
            <ExperimentsTab communityId={user?.communityId} />
          )}
        </PageInner>
      </Page>
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state) => ({
  user: state.storeUser,
});

export default connect(mapStateToProps, null)(AnalyticsScreen);
